import React, { useEffect, useMemo, useRef, useState } from "react";

// BETALUMINA_COLLAUDI_FRONT_FIX28_20261024
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";
const CHECKLIST_VERSION = "2026-10-25-CLEAN-V2";
const today = () => new Date().toISOString().slice(0, 10);

const P = (voce, categoria = "standard", unita = "") => ({
  voce, categoria, unita, esito: "", valore: "", note: "",
});

const BASE_CHECKLIST = {
  Antintrusione: [
    P("Centrale: stato normale, assenza guasti/anomalie e corretta identificazione delle segnalazioni"),
    P("Prova funzionale dei rivelatori e delle zone installate"),
    P("Verifica sabotaggio, tamper e supervisione dei dispositivi previsti"),
    P("Inserimento, disinserimento e parzializzazioni configurate"),
    P("Tempi di ingresso, uscita e temporizzazioni configurate"),
    P("Sirene e segnalazioni ottico-acustiche installate"),
    P("Notifiche, chiamate e trasmissioni configurate"),
    P("Alimentazione primaria e batteria di riserva"),
    P("Memoria eventi e data/ora della centrale"),
    P("Tastiere, radiocomandi, app e gestione remota consegnata al cliente"),
    P("Istruzioni di utilizzo e gestione essenziale illustrate al cliente"),
  ],
  Videosorveglianza: [
    P("Visualizzazione live di tutte le telecamere e corretta denominazione dei canali"),
    P("Inquadratura, orientamento e copertura delle aree previste"),
    P("Qualita immagine nelle condizioni di utilizzo previste"),
    P("Funzionamento giorno/notte e illuminazione IR, se presente"),
    P("Registrazione dei canali previsti"),
    P("Ricerca e riproduzione delle registrazioni"),
    P("Esportazione di un filmato di prova e verifica della riproduzione"),
    P("Data e ora coerenti tra telecamere, NVR/VMS e client"),
    P("Capacita di registrazione e periodo di conservazione configurato", "standard", "giorni"),
    P("Gestione perdita video/rete e segnalazioni di guasto disponibili"),
    P("Analisi video e regole evento configurate"),
    P("Accesso remoto e profili utente consegnati"),
    P("Alimentazioni PoE/alimentatori e continuita prevista"),
  ],
  "Controllo accessi": [
    P("Accesso consentito con credenziale autorizzata"),
    P("Accesso negato con credenziale non autorizzata o disabilitata"),
    P("Comando serratura, incontro o elettromagnete e corretta richiusura"),
    P("Pulsante di uscita e richiesta di uscita, se presenti"),
    P("Sensore porta e stato varco, se presenti"),
    P("Allarmi porta forzata o porta aperta oltre tempo, se configurati"),
    P("Registrazione eventi, data/ora e identificazione varco/utente"),
    P("Profili, fasce orarie e autorizzazioni configurate"),
    P("Funzionamento in assenza rete/server e successivo riallineamento, se previsto"),
    P("Alimentazione primaria e backup, se previsti"),
  ],
  Citofonia: [
    P("Chiamata da ogni posto esterno verso le utenze previste"),
    P("Comunicazione audio bidirezionale e intelligibilita"),
    P("Immagine video e inquadratura, se presenti"),
    P("Comando apertura porta/cancello e relè ausiliari"),
    P("Segnalazioni di chiamata, volume e modalita privacy/mute"),
    P("Rubrica, nominativi e instradamento chiamate"),
    P("App, inoltro chiamata e connettivita IP/GSM/LTE, se previsti"),
    P("Alimentazione e ripristino dopo interruzione"),
  ],
  Automazione: [
    P("Apertura e chiusura complete con arresto nelle posizioni previste"),
    P("Comandi da pulsanti, selettori, radiocomandi e sistemi collegati"),
    P("Fotocellule e dispositivi di presenza/sicurezza installati"),
    P("Bordi sensibili e dispositivi di protezione installati"),
    P("Rilevamento ostacolo e inversione/arresto configurati"),
    P("Lampeggiante, segnalazioni e prelampeggio, se presenti"),
    P("Sblocco manuale e manovra in emergenza"),
    P("Finecorsa, rallentamenti e temporizzazioni"),
    P("Richiusura automatica e logiche accessorie configurate"),
    P("Istruzioni di utilizzo e sblocco illustrate al cliente"),
  ],
  "Rete / LAN": [
    P("Link fisico delle porte e collegamento degli apparati previsti"),
    P("Continuita e corretta attestazione dei collegamenti"),
    P("Etichettatura prese, patch panel e corrispondenza delle tratte"),
    P("Indirizzamento IP, DHCP/statico, gateway e DNS previsti"),
    P("Connettivita tra VLAN/reti previste"),
    P("Connettivita Internet, se prevista"),
    P("PoE e alimentazione degli apparati alimentati da rete"),
    P("Velocita di collegamento e stabilita delle connessioni"),
    P("Configurazioni essenziali e credenziali consegnate"),
  ],
  "Antincendio / IRAI": [
    P("Centrale: stato normale, assenza guasti e corretta gestione delle segnalazioni"),
    P("Rivelatori installati: attivazione e corretta identificazione"),
    P("Pulsanti manuali: attivazione e corretta identificazione"),
    P("Dispositivi ottico-acustici/vocali: attivazione e segnalazione"),
    P("Moduli di ingresso/uscita e attuazioni collegate"),
    P("Segnalazioni di guasto delle linee/dispositivi monitorati"),
    P("Alimentazione primaria e batterie di riserva"),
    P("Trasmissione allarmi/guasti a sistemi remoti, se prevista"),
    P("Reset e ritorno alla condizione normale dopo le prove"),
    P("Logiche causa-effetto, ritardi e sequenze configurate"),
    P("Istruzioni operative illustrate al responsabile/addetti"),
  ],
};

const NORMATIVE_CHECKLIST = {
  Antintrusione: [
    P("Verifica delle funzioni previste dal progetto e dal livello di prestazione dell'impianto", "normativa"),
    P("Prova dei rivelatori e corretta identificazione degli eventi in centrale", "normativa"),
    P("Verifica delle protezioni contro sabotaggio previste dal sistema", "normativa"),
    P("Verifica dei dispositivi di allarme locali previsti", "normativa"),
    P("Verifica dei sistemi di trasmissione allarme previsti", "normativa"),
    P("Verifica alimentazione primaria, riserva e segnalazione dei guasti di alimentazione", "normativa"),
    P("Verifica delle procedure di inserimento/disinserimento previste", "normativa"),
    P("Verifica finale delle funzioni consegnate al committente", "normativa"),
  ],
  Videosorveglianza: [
    P("Verifica della copertura e del campo di vista rispetto ai requisiti operativi definiti", "normativa"),
    P("Verifica della qualita immagine rispetto allo scopo operativo previsto", "normativa"),
    P("Verifica delle prestazioni nelle condizioni di illuminazione previste", "normativa"),
    P("Verifica registrazione, ricerca e riproduzione delle immagini", "normativa"),
    P("Verifica data/ora e sincronizzazione temporale del sistema", "normativa"),
    P("Verifica esportazione delle registrazioni quando richiesta dal sistema", "normativa"),
    P("Verifica capacita di archiviazione rispetto al periodo configurato", "normativa", "giorni"),
    P("Verifica segnalazioni di guasto/perdita video previste dal progetto", "normativa"),
  ],
  "Controllo accessi": [
    P("Verifica autorizzazione e rifiuto delle credenziali sui varchi previsti", "normativa"),
    P("Verifica attuazione e stato dei varchi controllati", "normativa"),
    P("Verifica registrazione degli eventi e identificazione del varco", "normativa"),
    P("Verifica delle funzioni di uscita e delle segnalazioni porta previste", "normativa"),
    P("Verifica delle interfacce con altri sistemi di sicurezza previste dal progetto", "normativa"),
    P("Verifica del comportamento in assenza comunicazione/rete quando previsto", "normativa"),
    P("Verifica alimentazione di riserva quando prevista", "normativa"),
  ],
  Citofonia: [
    P("Verifica delle chiamate e della comunicazione sulle utenze previste", "normativa"),
    P("Verifica dei comandi di apertura previsti dal sistema", "normativa"),
    P("Verifica delle funzioni video previste", "normativa"),
    P("Verifica delle funzioni e interfacce consegnate all'utilizzatore", "normativa"),
  ],
  Automazione: [
    P("Verifica dei dispositivi di protezione e rilevamento presenza installati", "normativa"),
    P("Verifica del comportamento dell'automazione in presenza di ostacolo", "normativa"),
    P("Verifica delle forze operative nei punti applicabili quando richiesta", "normativa", "N"),
    P("Verifica arresto, inversione e riavvio secondo la configurazione prevista", "normativa"),
    P("Verifica sblocco manuale e manovra in emergenza", "normativa"),
    P("Verifica segnalazioni e avvertimenti previsti", "normativa"),
    P("Verifica finale delle funzioni di sicurezza della chiusura motorizzata", "normativa"),
  ],
  "Rete / LAN": [
    P("Verifica strumentale del cablaggio permanente/canale quando prevista dalla consegna", "normativa"),
    P("Verifica continuita, mappatura e corretta terminazione delle coppie", "normativa"),
    P("Verifica dei parametri di trasmissione richiesti per la categoria/classe dichiarata", "normativa"),
    P("Verifica identificazione ed etichettatura dei collegamenti", "normativa"),
    P("Verifica documentazione dei risultati di misura quando prevista", "normativa"),
  ],
  "Antincendio / IRAI": [
    P("Verifica della centrale e delle segnalazioni di allarme e guasto previste", "normativa"),
    P("Prova dei rivelatori installati e corretta identificazione", "normativa"),
    P("Prova dei pulsanti manuali installati", "normativa"),
    P("Prova dei dispositivi di allarme ottico-acustici/vocali installati", "normativa"),
    P("Prova delle attuazioni e delle logiche causa-effetto previste", "normativa"),
    P("Verifica delle segnalazioni di guasto delle linee e dei dispositivi monitorati", "normativa"),
    P("Verifica alimentazione primaria e alimentazione di riserva", "normativa"),
    P("Verifica delle trasmissioni remote di allarme/guasto previste", "normativa"),
    P("Verifica reset e ripristino alla condizione normale", "normativa"),
    P("Registrazione degli esiti delle prove e aggiornamento della documentazione prevista", "normativa"),
  ],
};

const emptyForm = () => ({
  id: null, sito_id: "", intervento_id: "", preventivo_id: "", data_collaudo: today(), tipo_impianto: "",
  titolo: "Verbale di collaudo e consegna impianto", descrizione_impianto: "", apparecchiature: "", prove: [],
  anomalie: "", documentazione_consegnata: "", formazione_cliente: "", esito: "", note: "", stato: "Bozza",
  visibile_portale: 1, qr_recensione_enabled: 1, checklist_version: CHECKLIST_VERSION,
});

const authHeaders = (json = false) => {
  const token = localStorage.getItem("token");
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { "Content-Type": "application/json" } : {}) };
};
const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("it-IT");
};
const inputStyle = { width: "100%", boxSizing: "border-box", border: "1px solid #d7e0e8", borderRadius: 9, padding: "10px 11px", fontSize: 14, background: "#fff" };
const labelStyle = { display: "grid", gap: 6, fontSize: 12, fontWeight: 700, color: "#334155" };
const button = (primary = false, danger = false) => ({
  border: `1px solid ${danger ? "#d92d20" : primary ? "#147957" : "#d4dde5"}`,
  background: danger ? "#fff" : primary ? "#147957" : "#fff",
  color: danger ? "#b42318" : primary ? "#fff" : "#334155", borderRadius: 9, padding: "9px 13px", fontWeight: 700, cursor: "pointer",
});

export default function ClienteCollaudi({ clienteId, cliente = {} }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [data, setData] = useState({ collaudi: [], siti: [], interventi: [], preventivi: [], config: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [normChoice, setNormChoice] = useState("");
  const [sigOpen, setSigOpen] = useState(false);
  const [sigNome, setSigNome] = useState("");
  const [sigDataUrl, setSigDataUrl] = useState(null);
  const [sigPresent, setSigPresent] = useState(false);
  const [sigHasInk, setSigHasInk] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const collaudi = Array.isArray(data.collaudi) ? data.collaudi : [];
  const siti = Array.isArray(data.siti) ? data.siti : [];
  const interventi = Array.isArray(data.interventi) ? data.interventi : [];
  const preventivi = Array.isArray(data.preventivi) ? data.preventivi : [];

  const load = async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/clienti/${clienteId}/collaudi`, { headers: authHeaders() });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload?.message || "Errore caricamento collaudi");
      setData(payload);
    } catch (err) { setMessage(err.message || "Errore caricamento collaudi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clienteId]);

  const siteById = useMemo(() => {
    const map = new Map(); siti.forEach((s) => map.set(String(s.id), s)); return map;
  }, [siti]);
  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const loadSignature = async (id) => {
    if (!id) { setSigPresent(false); setSigNome(""); setSigDataUrl(null); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${id}/firma-cliente`, { headers: authHeaders() });
      const p = await res.json();
      if (!res.ok) throw new Error(p?.message || "Errore firma");
      setSigPresent(Boolean(p.presente)); setSigNome(p.nome || ""); setSigDataUrl(p.signature || null);
    } catch (e) { setSigPresent(false); setSigNome(""); setSigDataUrl(null); }
  };

  const nuovo = () => { setForm(emptyForm()); setNormChoice(""); setSigPresent(false); setSigNome(""); setSigDataUrl(null); setMessage(""); setOpen(true); };
  const modifica = async (item) => {
    if (item.finalized_at) { setMessage("Il collaudo è finalizzato: usa 'Riapri' prima di modificarlo."); return; }
    setForm({ ...emptyForm(), ...item, sito_id: item.sito_id ? String(item.sito_id) : "", intervento_id: item.intervento_id ? String(item.intervento_id) : "", preventivo_id: item.preventivo_id ? String(item.preventivo_id) : "", data_collaudo: item.data_collaudo ? String(item.data_collaudo).slice(0, 10) : today(), prove: Array.isArray(item.prove) ? item.prove : [], visibile_portale: Number(item.visibile_portale) === 0 ? 0 : 1, qr_recensione_enabled: Number(item.qr_recensione_enabled) === 0 ? 0 : 1 });
    setNormChoice(""); await loadSignature(item.id); setMessage(""); setOpen(true);
  };

  const caricaProveStandard = () => {
    const rows = BASE_CHECKLIST[form.tipo_impianto];
    if (!rows) { setMessage("Seleziona prima una tipologia impianto."); return; }
    if (form.prove.length && !window.confirm("Sostituire le prove presenti con le prove standard della tipologia selezionata?")) return;
    setField("prove", rows.map((row) => ({ ...row })));
    setField("checklist_version", CHECKLIST_VERSION);
  };
  const normativeOptions = NORMATIVE_CHECKLIST[form.tipo_impianto] || [];
  const addNormative = () => {
    const idx = Number(normChoice);
    if (!Number.isInteger(idx) || idx < 0 || idx >= normativeOptions.length) {
      setMessage("Seleziona una prova normativa dal menu.");
      return;
    }
    const row = normativeOptions[idx];
    if ((form.prove || []).some((p) => String(p.voce || "").trim() === String(row.voce || "").trim())) {
      setMessage("Questa prova e gia presente nel collaudo.");
      return;
    }
    setField("prove", [...(form.prove || []), { ...row }]);
    setField("checklist_version", CHECKLIST_VERSION);
    setNormChoice("");
    setMessage("");
  };
  const addProva = () => setField("prove", [...(form.prove || []), P("", "personalizzata")]);
  const updateProva = (index, key, value) => setField("prove", form.prove.map((row, i) => i === index ? { ...row, [key]: value } : row));
  const removeProva = (index) => setField("prove", form.prove.filter((_, i) => i !== index));
  const categoriaLabel = (row) => row?.categoria === "normativa" ? "Normativa" : row?.categoria === "personalizzata" ? "Personalizzata" : "Standard";

  const save = async () => {
    if (busy) return;
    setBusy(true); setMessage("");
    try {
      const isEdit = Boolean(form.id);
      const res = await fetch(isEdit ? `${API_BASE_URL}/collaudi/${form.id}` : `${API_BASE_URL}/clienti/${clienteId}/collaudi`, { method: isEdit ? "PUT" : "POST", headers: authHeaders(true), body: JSON.stringify({ ...form, stato: "Bozza" }) });
      const p = await res.json();
      if (!res.ok) throw new Error(p?.message || "Errore salvataggio collaudo");
      setOpen(false); setMessage("Collaudo salvato come bozza."); await load();
    } catch (err) { setMessage(err.message || "Errore salvataggio collaudo"); }
    finally { setBusy(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare il collaudo n. ${item.id}?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${item.id}`, { method: "DELETE", headers: authHeaders() });
      const p = await res.json(); if (!res.ok) throw new Error(p?.message || "Errore eliminazione");
      setMessage("Collaudo eliminato."); await load();
    } catch (err) { setMessage(err.message || "Errore eliminazione collaudo"); }
  };

  const pdf = async (item) => {
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${item.id}/pdf`, { headers: authHeaders() });
      if (!res.ok) { let msg = "Errore generazione PDF"; try { const p = await res.json(); msg = p?.message || msg; } catch (_) {} throw new Error(msg); }
      const blob = await res.blob(); const url = URL.createObjectURL(blob); window.open(url, "_blank", "noopener,noreferrer"); window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setMessage(err.message || "Errore generazione PDF"); }
  };

  const finalizza = async (item) => {
    let allow = 0;
    if (!item.firma_cliente_presente) {
      if (!window.confirm("La firma cliente non è stata acquisita. Vuoi finalizzare e archiviare comunque il verbale senza firma cliente?")) return;
      allow = 1;
    }
    if (!window.confirm(`Finalizzare il collaudo n. ${item.id}? Il PDF verrà archiviato nei documenti cliente.`)) return;
    setBusy(true); setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${item.id}/finalizza`, { method: "POST", headers: authHeaders(true), body: JSON.stringify({ allow_without_signature: allow }) });
      const p = await res.json(); if (!res.ok) throw new Error(p?.message || "Errore finalizzazione");
      setMessage(`Collaudo n. ${item.id} finalizzato e archiviato.`); await load();
    } catch (e) { setMessage(e.message || "Errore finalizzazione"); }
    finally { setBusy(false); }
  };

  const riapri = async (item) => {
    if (!window.confirm(`Riaprire il collaudo n. ${item.id}? Il PDF archiviato verrà disattivato/rimosso e il verbale tornerà in bozza.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${item.id}/riapri`, { method: "POST", headers: authHeaders(true), body: "{}" });
      const p = await res.json(); if (!res.ok) throw new Error(p?.message || "Errore riapertura");
      setMessage("Collaudo riaperto in bozza."); await load();
    } catch (e) { setMessage(e.message || "Errore riapertura"); }
  };

  const togglePortal = async (item) => {
    const next = Number(item.visibile_portale) === 0 ? 1 : 0;
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${item.id}/visibilita-portale`, { method: "POST", headers: authHeaders(true), body: JSON.stringify({ visibile_portale: next }) });
      const p = await res.json(); if (!res.ok) throw new Error(p?.message || "Errore visibilità Portale");
      setMessage(next ? "Collaudo visibile nel Portale Cliente." : "Collaudo nascosto dal Portale Cliente."); await load();
    } catch (e) { setMessage(e.message || "Errore visibilità Portale"); }
  };

  const prepareCanvas = (dataUrl = null) => {
    const canvas = canvasRef.current; if (!canvas) return;
    canvas.width = 960; canvas.height = 360;
    const ctx = canvas.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#13263a"; ctx.lineWidth = 4;
    setSigHasInk(false);
    if (dataUrl) { const img = new Image(); img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); setSigHasInk(true); }; img.src = dataUrl; }
  };
  useEffect(() => { if (sigOpen) window.setTimeout(() => prepareCanvas(sigDataUrl), 30); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sigOpen]);
  const point = (e) => { const c = canvasRef.current; const r = c.getBoundingClientRect(); return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) }; };
  const down = (e) => { if (!canvasRef.current) return; e.preventDefault(); const p = point(e); drawingRef.current = true; lastRef.current = p; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {} };
  const move = (e) => { if (!drawingRef.current || !canvasRef.current) return; e.preventDefault(); const p = point(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(lastRef.current.x, lastRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); lastRef.current = p; setSigHasInk(true); };
  const up = (e) => { drawingRef.current = false; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {} };
  const clearSig = () => { prepareCanvas(null); setSigDataUrl(null); };
  const saveSig = async () => {
    if (!form.id) { setMessage("Salva prima il collaudo come bozza, poi acquisisci la firma."); return; }
    if (!sigNome.trim()) { setMessage("Indica nome e cognome del firmatario."); return; }
    if (!sigHasInk || !canvasRef.current) { setMessage("Inserisci la firma nel riquadro."); return; }
    let signature = canvasRef.current.toDataURL("image/jpeg", 0.82);
    if (signature.length > 95000) signature = canvasRef.current.toDataURL("image/jpeg", 0.68);
    if (signature.length > 95000) signature = canvasRef.current.toDataURL("image/jpeg", 0.52);
    if (signature.length > 95000) { setMessage("Firma troppo grande: cancella e riprova con un tratto più semplice."); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${form.id}/firma-cliente`, { method: "POST", headers: authHeaders(true), body: JSON.stringify({ nome: sigNome.trim(), signature }) });
      const p = await res.json(); if (!res.ok) throw new Error(p?.message || "Errore salvataggio firma");
      setSigPresent(true); setSigDataUrl(signature); setSigOpen(false); setMessage("Firma cliente salvata."); await load();
    } catch (e) { setMessage(e.message || "Errore salvataggio firma"); }
  };
  const deleteSig = async () => {
    if (!form.id || !window.confirm("Eliminare la firma cliente salvata?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/collaudi/${form.id}/firma-cliente`, { method: "DELETE", headers: authHeaders() });
      const p = await res.json(); if (!res.ok) throw new Error(p?.message || "Errore eliminazione firma");
      setSigPresent(false); setSigNome(""); setSigDataUrl(null); setMessage("Firma cliente eliminata."); await load();
    } catch (e) { setMessage(e.message || "Errore eliminazione firma"); }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <style>{`
        .collaudo-proof-row{display:grid;grid-template-columns:minmax(260px,2fr) 110px minmax(130px,.65fr) minmax(200px,1fr) auto;gap:8px;align-items:start}
        @media(max-width:1050px){.collaudo-proof-row{grid-template-columns:1fr 110px}.collaudo-proof-row .wide{grid-column:1/-1}}
        .collaudo-modal-bg{position:fixed;inset:0;background:rgba(15,23,42,.5);z-index:45000;display:flex;align-items:flex-start;justify-content:center;overflow:auto;padding:32px 14px}
        .collaudo-modal{width:min(1180px,96vw);background:#f8fafc;border-radius:16px;box-shadow:0 24px 70px rgba(15,23,42,.25);padding:18px}
        .collaudo-sig-bg{position:fixed;inset:0;background:rgba(2,6,23,.78);z-index:60000;display:flex;align-items:center;justify-content:center;padding:12px}
        .collaudo-sig-panel{width:min(1050px,98vw);background:#fff;border-radius:16px;padding:16px}
        .collaudo-sig-canvas{width:100%;height:auto;aspect-ratio:8/3;border:2px solid #cbd5e1;border-radius:10px;touch-action:none;background:#fff}
      `}</style>

      <div style={{ border: "1px solid #dbe3ea", borderRadius: 14, padding: 18, background: "#fff", display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#17324d" }}>Verbali di collaudo</div>
          <div style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>Checklist tecniche per tipologia, firma cliente, PDF BetaLumina, finalizzazione e archivio documenti.</div>
        </div>
        <button type="button" style={button(true)} onClick={nuovo}>+ Nuovo collaudo</button>
      </div>

      {message && <div style={{ borderRadius: 10, padding: "10px 12px", background: "#fff7ed", border: "1px solid #dfe7ed", color: "#334155", fontSize: 13 }}>{message}</div>}

      {loading ? <div style={{ padding: 18, color: "#64748b" }}>Caricamento collaudi...</div> : collaudi.length === 0 ? (
        <div style={{ padding: 20, border: "1px dashed #cbd5e1", borderRadius: 12, color: "#64748b", background: "#fff" }}>Nessun collaudo registrato.</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {collaudi.map((item) => (
            <div key={item.id} style={{ border: "1px solid #dbe3ea", borderRadius: 12, background: "#fff", padding: 14, display: "grid", gap: 9 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 800, color: "#17324d" }}>Collaudo n. {item.id}  -  {item.tipo_impianto || "Impianto non indicato"}</div>
                  <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 4 }}>{formatDate(item.data_collaudo)}{item.sito_nome ? `  -  ${item.sito_nome}` : ""}{item.esito ? `  -  ${item.esito}` : ""}</div>
                </div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ padding: "5px 8px", borderRadius: 999, background: item.finalized_at ? "#e8f6ef" : "#f1f5f9", color: item.finalized_at ? "#13795b" : "#475569", fontSize: 11, fontWeight: 800 }}>{item.finalized_at ? "FINALIZZATO" : "BOZZA"}</span>
                  <span style={{ padding: "5px 8px", borderRadius: 999, background: item.firma_cliente_presente ? "#eef7ff" : "#f8fafc", color: "#475569", fontSize: 11, fontWeight: 700 }}>{item.firma_cliente_presente ? "Firma acquisita" : "Senza firma"}</span>
                  <span style={{ padding: "5px 8px", borderRadius: 999, background: Number(item.visibile_portale) !== 0 ? "#effaf5" : "#fff7ed", color: "#475569", fontSize: 11, fontWeight: 700 }}>Portale {Number(item.visibile_portale) !== 0 ? "Si" : "No"}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!item.finalized_at && <button type="button" style={button()} onClick={() => modifica(item)}>Modifica / firma</button>}
                <button type="button" style={button()} onClick={() => pdf(item)}>Anteprima PDF</button>
                {!item.finalized_at && <button type="button" style={button(true)} disabled={busy} onClick={() => finalizza(item)}>Finalizza e archivia</button>}
                {item.finalized_at && <button type="button" style={button()} onClick={() => togglePortal(item)}>Portale: {Number(item.visibile_portale) !== 0 ? "nascondi" : "mostra"}</button>}
                {item.finalized_at && <button type="button" style={button()} onClick={() => riapri(item)}>Riapri</button>}
                {!item.finalized_at && <button type="button" style={button(false, true)} onClick={() => remove(item)}>Elimina</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="collaudo-modal-bg">
          <div className="collaudo-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div><div style={{ fontSize: 18, fontWeight: 800, color: "#17324d" }}>{form.id ? `Collaudo n. ${form.id}` : "Nuovo collaudo"}</div><div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>Salva come bozza; la finalizzazione viene eseguita dalla scheda elenco.</div></div>
              <button type="button" style={button()} onClick={() => setOpen(false)}>Chiudi</button>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
                <label style={labelStyle}>Data collaudo<input type="date" style={inputStyle} value={form.data_collaudo || ""} onChange={(e) => setField("data_collaudo", e.target.value)} /></label>
                <label style={labelStyle}>Tipo impianto<select style={inputStyle} value={form.tipo_impianto || ""} onChange={(e) => { setField("tipo_impianto", e.target.value); setNormChoice(""); }}><option value="">Seleziona...</option>{Object.keys(BASE_CHECKLIST).map((x) => <option key={x} value={x}>{x}</option>)}<option value="Altro">Altro</option></select></label>
                <label style={labelStyle}>Sito<select style={inputStyle} value={form.sito_id || ""} onChange={(e) => { const id = e.target.value; const s = siteById.get(String(id)); setForm((prev) => ({ ...prev, sito_id: id, tipo_impianto: prev.tipo_impianto || s?.tipo_impianto || "" })); }}><option value="">Nessun sito specifico</option>{siti.map((s) => <option key={s.id} value={s.id}>{s.nome} - {s.indirizzo}</option>)}</select></label>
              </div>

              <label style={labelStyle}>Titolo documento<input style={inputStyle} value={form.titolo || ""} onChange={(e) => setField("titolo", e.target.value)} /></label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 }}>
                <label style={labelStyle}>Collega a intervento<select style={inputStyle} value={form.intervento_id || ""} onChange={(e) => setField("intervento_id", e.target.value)}><option value="">Nessun intervento</option>{interventi.map((i) => <option key={i.id} value={i.id}>{i.numero_foglio_intervento || `Intervento #${i.id}`} - {i.causale || i.descrizione || formatDate(i.dataEsecuzione || i.data)}</option>)}</select></label>
                <label style={labelStyle}>Collega a preventivo<select style={inputStyle} value={form.preventivo_id || ""} onChange={(e) => setField("preventivo_id", e.target.value)}><option value="">Nessun preventivo</option>{preventivi.map((p) => <option key={p.id} value={p.id}>Preventivo #{p.id} - {p.oggetto || formatDate(p.dataEmissione)}</option>)}</select></label>
              </div>
              <label style={labelStyle}>Descrizione impianto / lavori eseguiti<textarea rows={5} style={inputStyle} value={form.descrizione_impianto || ""} onChange={(e) => setField("descrizione_impianto", e.target.value)} /></label>
              <label style={labelStyle}>Apparecchiature principali installate<textarea rows={4} style={inputStyle} value={form.apparecchiature || ""} onChange={(e) => setField("apparecchiature", e.target.value)} /></label>

              <div style={{ border: "1px solid #dbe3ea", borderRadius: 12, padding: 14, background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 800, color: "#17324d" }}>Verifiche e prove</div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>Le prove standard sono funzionali. Le prove normative vengono aggiunte separatamente solo quando applicabili al lavoro eseguito.</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" style={button()} onClick={caricaProveStandard}>Carica prove standard</button>
                    <button type="button" style={button()} onClick={addProva}>+ Prova personalizzata</button>
                  </div>
                </div>

                {form.tipo_impianto && normativeOptions.length > 0 && (
                  <div style={{ marginTop: 12, padding: 12, border: "1px solid #dbe3ea", borderRadius: 10, background: "#f8fafc" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 7 }}>Aggiungi prova normativa applicabile</div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px,1fr) auto", gap: 8 }}>
                      <select style={inputStyle} value={normChoice} onChange={(e) => setNormChoice(e.target.value)}>
                        <option value="">Seleziona una prova...</option>
                        {normativeOptions.map((row, index) => <option key={`${form.tipo_impianto}-${index}`} value={index}>{row.voce}</option>)}
                      </select>
                      <button type="button" style={button(true)} onClick={addNormative}>+ Aggiungi</button>
                    </div>
                    <div style={{ marginTop: 6, color: "#64748b", fontSize: 11 }}>Nel PDF cliente viene riportata soltanto la prova eseguita e il relativo esito, senza riferimenti o numeri di norma.</div>
                  </div>
                )}

                <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                  {(form.prove || []).length === 0 ? <div style={{ padding: 14, color: "#64748b", fontSize: 12.5, background: "#f8fafc", borderRadius: 9 }}>Nessuna prova inserita.</div> : form.prove.map((row, index) => (
                    <div key={index} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 10, background: "#fbfdff" }}>
                      <div className="collaudo-proof-row">
                        <div className="wide">
                          <input style={inputStyle} placeholder="Verifica / prova" value={row.voce || ""} onChange={(e) => updateProva(index, "voce", e.target.value)} />
                          <div style={{ marginTop: 5, display: "inline-flex", padding: "3px 7px", borderRadius: 999, background: row.categoria === "normativa" ? "#eef7ff" : row.categoria === "personalizzata" ? "#fff7ed" : "#f1f5f9", color: "#475569", fontSize: 10.5, fontWeight: 700 }}>{categoriaLabel(row)}</div>
                        </div>
                        <select style={inputStyle} value={row.esito || ""} onChange={(e) => updateProva(index, "esito", e.target.value)}><option value="">-</option><option value="OK">OK</option><option value="NON OK">NON OK</option><option value="N.A.">N.A.</option></select>
                        <div><input style={inputStyle} placeholder="Valore misurato" value={row.valore || ""} onChange={(e) => updateProva(index, "valore", e.target.value)} />{row.unita && <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>Unita: {row.unita}</div>}</div>
                        <input className="wide" style={inputStyle} placeholder="Note / dettaglio prova" value={row.note || ""} onChange={(e) => updateProva(index, "note", e.target.value)} />
                        <button type="button" aria-label="Rimuovi prova" style={{ ...button(false, true), padding: "9px 10px" }} onClick={() => removeProva(index)}>X</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <label style={labelStyle}>Anomalie / prescrizioni / lavorazioni residue<textarea rows={4} style={inputStyle} value={form.anomalie || ""} onChange={(e) => setField("anomalie", e.target.value)} /></label>
              <label style={labelStyle}>Documentazione consegnata<textarea rows={3} style={inputStyle} value={form.documentazione_consegnata || ""} onChange={(e) => setField("documentazione_consegnata", e.target.value)} /></label>
              <label style={labelStyle}>Istruzioni / formazione effettuata al cliente<textarea rows={3} style={inputStyle} value={form.formazione_cliente || ""} onChange={(e) => setField("formazione_cliente", e.target.value)} /></label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
                <label style={labelStyle}>Esito finale<select style={inputStyle} value={form.esito || ""} onChange={(e) => setField("esito", e.target.value)}><option value="">Non indicato</option><option value="Positivo">Positivo</option><option value="Positivo con prescrizioni">Positivo con prescrizioni</option><option value="Non positivo">Non positivo</option></select></label>
                <label style={{ ...labelStyle, alignContent: "end" }}><span>Portale cliente</span><span style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 40 }}><input type="checkbox" checked={Number(form.visibile_portale) !== 0} onChange={(e) => setField("visibile_portale", e.target.checked ? 1 : 0)} />Visibile nel Portale dopo la finalizzazione</span></label>
                <label style={{ ...labelStyle, alignContent: "end" }}><span>Recensione Google</span><span style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 40 }}><input type="checkbox" checked={Number(form.qr_recensione_enabled) !== 0} onChange={(e) => setField("qr_recensione_enabled", e.target.checked ? 1 : 0)} />Inserisci QR nel PDF</span></label>
              </div>
              <label style={labelStyle}>Note finali<textarea rows={4} style={inputStyle} value={form.note || ""} onChange={(e) => setField("note", e.target.value)} /></label>

              <div style={{ border: "1px solid #dbe3ea", borderRadius: 12, padding: 14, background: "#fff", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div><div style={{ fontWeight: 800, color: "#17324d" }}>Firma cliente</div><div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>{form.id ? (sigPresent ? `Firma presente${sigNome ? `  -  ${sigNome}` : ""}` : "Firma non ancora acquisita") : "Salva prima la bozza per abilitare la firma."}</div></div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><button type="button" style={button()} disabled={!form.id} onClick={() => setSigOpen(true)}>{sigPresent ? "Modifica firma" : "Firma cliente"}</button>{sigPresent && <button type="button" style={button(false, true)} onClick={deleteSig}>Elimina firma</button>}</div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, flexWrap: "wrap" }}><button type="button" style={button()} onClick={() => setOpen(false)}>Annulla</button><button type="button" style={{ ...button(true), opacity: busy ? 0.65 : 1 }} disabled={busy} onClick={save}>{busy ? "Salvataggio..." : "Salva bozza"}</button></div>
            </div>
          </div>
        </div>
      )}

      {sigOpen && (
        <div className="collaudo-sig-bg">
          <div className="collaudo-sig-panel">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}><div><div style={{ fontSize: 18, fontWeight: 800, color: "#17324d" }}>Firma cliente</div><div style={{ color: "#64748b", fontSize: 12 }}>Firma con dito o penna su tablet/telefono, oppure con mouse.</div></div><button type="button" style={button()} onClick={() => setSigOpen(false)}>Chiudi</button></div>
            <label style={labelStyle}>Nome e cognome firmatario<input style={inputStyle} value={sigNome} onChange={(e) => setSigNome(e.target.value)} /></label>
            <div style={{ marginTop: 10 }}><canvas ref={canvasRef} className="collaudo-sig-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} /></div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 10 }}><button type="button" style={button()} onClick={clearSig}>Cancella tratto</button><div style={{ display: "flex", gap: 8 }}><button type="button" style={button()} onClick={() => setSigOpen(false)}>Annulla</button><button type="button" style={button(true)} onClick={saveSig}>Salva firma</button></div></div>
          </div>
        </div>
      )}
    </div>
  );
}

